import { useState, useEffect } from 'react';
import { BASE, authFetch } from '../constants';
import PageLayout from '../components/ui/PageLayout';
import ActionButton from '../components/ui/ActionButton';

interface SSOConfig {
  id: number;
  provider_type: string;
  provider_name: string;
  idp_entity_id?: string;
  idp_sso_url?: string;
  client_id?: string;
  authorization_url?: string;
  auto_create_users: boolean;
  default_role: string;
  force_sso: boolean;
  is_active: boolean;
  created_at: string;
}

interface ValidationResult {
  valid: boolean;
  checks: { check: string; status: string }[];
  errors: string[];
}

const PROVIDER_TEMPLATES: Record<string, Partial<SSOConfig & { authorization_url: string; token_url: string; jwks_url: string; userinfo_url: string; scopes: string }>> = {
  AZURE_AD: {
    provider_type: 'AZURE_AD',
    authorization_url: 'https://login.microsoftonline.com/{tenant}/oauth2/v2.0/authorize',
    token_url: 'https://login.microsoftonline.com/{tenant}/oauth2/v2.0/token',
    jwks_url: 'https://login.microsoftonline.com/{tenant}/discovery/v2.0/keys',
    userinfo_url: 'https://graph.microsoft.com/oidc/userinfo',
    scopes: 'openid email profile',
  },
  OKTA: {
    provider_type: 'OKTA',
    authorization_url: 'https://{domain}.okta.com/oauth2/default/v1/authorize',
    token_url: 'https://{domain}.okta.com/oauth2/default/v1/token',
    jwks_url: 'https://{domain}.okta.com/oauth2/default/v1/keys',
    userinfo_url: 'https://{domain}.okta.com/oauth2/default/v1/userinfo',
    scopes: 'openid email profile groups',
  },
  GOOGLE: {
    provider_type: 'GOOGLE',
    authorization_url: 'https://accounts.google.com/o/oauth2/v2/auth',
    token_url: 'https://oauth2.googleapis.com/token',
    jwks_url: 'https://www.googleapis.com/oauth2/v3/certs',
    userinfo_url: 'https://openidconnect.googleapis.com/v1/userinfo',
    scopes: 'openid email profile',
  },
};

const API = BASE;

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '7px 12px', fontSize: 13,
  border: '1px solid var(--border-subtle)', borderRadius: 6,
  background: 'var(--bg-card)', color: 'var(--text-primary)',
  outline: 'none', marginTop: 4,
};

export default function SSOSettings() {
  const [configs, setConfigs] = useState<SSOConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [testResult, setTestResult] = useState<ValidationResult | null>(null);
  const [error, setError] = useState('');

  // Form state
  const [formData, setFormData] = useState<Record<string, any>>({
    provider_type: 'OIDC',
    provider_name: '',
    auto_create_users: true,
    default_role: 'L3',
    force_sso: false,
    session_timeout_minutes: 480,
    scopes: 'openid email profile',
    attribute_mapping: '{}',
    role_mapping: '{}',
  });

  useEffect(() => { fetchConfigs(); }, []);

  const fetchConfigs = async () => {
    try {
      const resp = await authFetch(`${API}/sso/configuration`);
      if (resp.ok) {
        const data = await resp.json();
        setConfigs(data.configurations || []);
      }
    } catch { /* ignore */ }
    setLoading(false);
  };

  const applyTemplate = (type: string) => {
    const tpl = PROVIDER_TEMPLATES[type];
    if (tpl) {
      setFormData(prev => ({ ...prev, ...tpl, provider_type: type }));
    } else {
      setFormData(prev => ({ ...prev, provider_type: type }));
    }
  };

  const handleCreate = async () => {
    setError('');
    try {
      const payload = { ...formData };
      if (typeof payload.attribute_mapping === 'string') {
        payload.attribute_mapping = JSON.parse(payload.attribute_mapping);
      }
      if (typeof payload.role_mapping === 'string') {
        payload.role_mapping = JSON.parse(payload.role_mapping);
      }
      const resp = await authFetch(`${API}/sso/configure`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (resp.ok) {
        setShowForm(false);
        fetchConfigs();
      } else {
        const data = await resp.json();
        setError(data.detail || 'Failed to create SSO config');
      }
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this SSO configuration?')) return;
    const resp = await authFetch(`${API}/sso/configure/${id}`, { method: 'DELETE' });
    if (resp.ok) fetchConfigs();
  };

  const handleTest = async (id: number) => {
    setTestResult(null);
    const resp = await authFetch(`${API}/sso/test/${id}`, { method: 'POST' });
    if (resp.ok) {
      setTestResult(await resp.json());
    }
  };

  if (loading) return (
    <PageLayout title="SSO Configuration" subtitle="Manage single sign-on providers">
      <div style={{ padding: 24, color: 'var(--text-hint)' }}>Loading SSO configurations...</div>
    </PageLayout>
  );

  return (
    <PageLayout
      title="SSO Configuration"
      subtitle="Manage single sign-on providers"
      action={{ label: showForm ? 'Cancel' : '+ Add SSO Provider', onClick: () => setShowForm(!showForm) }}
    >
      {error && (
        <div style={{ padding: 12, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 6, marginBottom: 16, color: '#dc2626', fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* Create Form */}
      {showForm && (
        <div style={{ padding: 20, background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 8, marginBottom: 20 }}>
          <h3 style={{ marginTop: 0, fontSize: 16, fontWeight: 700, color: 'var(--navy-900)' }}>New SSO Provider</h3>

          {/* Provider type selector */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {['AZURE_AD', 'OKTA', 'GOOGLE', 'SAML', 'OIDC'].map(type => (
              <button
                key={type}
                onClick={() => applyTemplate(type)}
                style={{
                  padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                  background: formData.provider_type === type ? '#1e3a5f' : 'var(--bg-card)',
                  color: formData.provider_type === type ? '#fff' : 'var(--text-secondary)',
                  border: `1px solid ${formData.provider_type === type ? '#1e3a5f' : 'var(--border-subtle)'}`,
                }}
              >
                {type.replace('_', ' ')}
              </button>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>
              Provider Name
              <input value={formData.provider_name || ''} onChange={e => setFormData(p => ({ ...p, provider_name: e.target.value }))} style={inputStyle} />
            </label>

            {formData.provider_type === 'SAML' ? (
              <>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>IdP Entity ID<input value={formData.idp_entity_id || ''} onChange={e => setFormData(p => ({ ...p, idp_entity_id: e.target.value }))} style={inputStyle} /></label>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>IdP SSO URL<input value={formData.idp_sso_url || ''} onChange={e => setFormData(p => ({ ...p, idp_sso_url: e.target.value }))} style={inputStyle} /></label>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>IdP SLO URL<input value={formData.idp_slo_url || ''} onChange={e => setFormData(p => ({ ...p, idp_slo_url: e.target.value }))} style={inputStyle} /></label>
              </>
            ) : (
              <>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Client ID<input value={formData.client_id || ''} onChange={e => setFormData(p => ({ ...p, client_id: e.target.value }))} style={inputStyle} /></label>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Client Secret<input type="password" value={formData.client_secret || ''} onChange={e => setFormData(p => ({ ...p, client_secret: e.target.value }))} style={inputStyle} /></label>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Authorization URL<input value={formData.authorization_url || ''} onChange={e => setFormData(p => ({ ...p, authorization_url: e.target.value }))} style={inputStyle} /></label>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Token URL<input value={formData.token_url || ''} onChange={e => setFormData(p => ({ ...p, token_url: e.target.value }))} style={inputStyle} /></label>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>JWKS URL<input value={formData.jwks_url || ''} onChange={e => setFormData(p => ({ ...p, jwks_url: e.target.value }))} style={inputStyle} /></label>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Scopes<input value={formData.scopes || ''} onChange={e => setFormData(p => ({ ...p, scopes: e.target.value }))} style={inputStyle} /></label>
              </>
            )}

            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Default Role
              <select value={formData.default_role} onChange={e => setFormData(p => ({ ...p, default_role: e.target.value }))} style={inputStyle}>
                <option value="L2">L2 (Viewer)</option>
                <option value="L3">L3 (Auditor)</option>
                <option value="L4">L4 (Reviewer)</option>
                <option value="L5">L5 (Controller)</option>
              </select>
            </label>

            <div style={{ display: 'flex', gap: 16, alignItems: 'center', paddingTop: 20 }}>
              <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}><input type="checkbox" checked={formData.auto_create_users} onChange={e => setFormData(p => ({ ...p, auto_create_users: e.target.checked }))} /> Auto-create users</label>
              <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}><input type="checkbox" checked={formData.force_sso} onChange={e => setFormData(p => ({ ...p, force_sso: e.target.checked }))} /> Force SSO</label>
            </div>
          </div>

          {/* SAML certificate */}
          {formData.provider_type === 'SAML' && (
            <label style={{ display: 'block', marginTop: 12, fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>
              IdP Certificate (X.509 PEM)
              <textarea value={formData.idp_certificate || ''} onChange={e => setFormData(p => ({ ...p, idp_certificate: e.target.value }))}
                rows={4} style={{ ...inputStyle, fontFamily: 'monospace', fontSize: 12 }} />
            </label>
          )}

          {/* Role mapping */}
          <label style={{ display: 'block', marginTop: 12, fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>
            Role Mapping (JSON)
            <textarea value={formData.role_mapping} onChange={e => setFormData(p => ({ ...p, role_mapping: e.target.value }))}
              rows={3} placeholder='{"CFO_Group": "L7", "Controller_Group": "L5"}'
              style={{ ...inputStyle, fontFamily: 'monospace', fontSize: 12 }} />
          </label>

          <div style={{ marginTop: 16 }}>
            <ActionButton label="Create SSO Configuration" onClick={handleCreate} variant="success" />
          </div>
        </div>
      )}

      {/* Test result */}
      {testResult && (
        <div style={{
          padding: 12, borderRadius: 6, marginBottom: 16,
          background: testResult.valid ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)',
          border: `1px solid ${testResult.valid ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)'}`,
        }}>
          <strong style={{ color: testResult.valid ? 'var(--positive)' : 'var(--negative)' }}>{testResult.valid ? 'Valid' : 'Invalid'}</strong>
          {testResult.checks.map((c, i) => <div key={i} style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{c.check}: {c.status}</div>)}
          {testResult.errors.map((e, i) => <div key={i} style={{ fontSize: 13, color: '#dc2626' }}>{e}</div>)}
        </div>
      )}

      {/* Config list */}
      {configs.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-hint)', background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)' }}>
          No SSO configurations. Click "Add SSO Provider" to get started.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {configs.map(cfg => (
            <div key={cfg.id} style={{ padding: 16, border: '1px solid var(--border-subtle)', borderRadius: 8, background: 'var(--bg-card)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <strong style={{ fontSize: 16, color: 'var(--navy-900)' }}>{cfg.provider_name}</strong>
                  <span style={{ marginLeft: 8, padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                    background: cfg.provider_type === 'SAML' ? 'rgba(37,99,235,0.1)' : 'rgba(245,158,11,0.1)',
                    color: cfg.provider_type === 'SAML' ? '#2563eb' : '#92400e',
                  }}>{cfg.provider_type}</span>
                  {cfg.force_sso && <span style={{ marginLeft: 8, padding: '2px 8px', borderRadius: 12, fontSize: 11, background: 'rgba(239,68,68,0.08)', color: '#dc2626', fontWeight: 600 }}>Force SSO</span>}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <ActionButton label="Test" onClick={() => handleTest(cfg.id)} variant="secondary" size="sm" />
                  <ActionButton label="Delete" onClick={() => handleDelete(cfg.id)} variant="danger" size="sm" />
                </div>
              </div>
              <div style={{ marginTop: 8, fontSize: 13, color: 'var(--text-hint)' }}>
                {cfg.auto_create_users ? 'Auto-create users' : 'Manual user creation'} | Default role: {cfg.default_role} | Created: {new Date(cfg.created_at).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      )}
    </PageLayout>
  );
}
